
// ── Helpers for e-signature flow ──────────────────────────────────
export function normalizePhoneForWa(phone){
  // Saudi numbers: convert 05XXXXXXXX → 9665XXXXXXXX, strip non-digits
  let p = String(phone||"").replace(/\D/g,"");
  if (p.startsWith("00966")) p = p.slice(2);
  else if (p.startsWith("966")) {/* ok */}
  else if (p.startsWith("0"))  p = "966" + p.slice(1);
  else if (p.startsWith("5"))  p = "966" + p;
  return p;
}
// Removed 2026-09-09: encodeInvoicePayload / decodeInvoicePayload / buildSignUrl.
// They built /sign?d=<base64> links carrying the customer's name, phone and
// national ID in the URL — readable by anyone who saw the link — and buildSignUrl
// additionally embedded the Supabase key. Sign links are now short tokens whose
// payload never leaves the server.

// buildCompactSignUrl removed 2026-09-09 — it was the "last-resort fallback" that
// leaked customer PII into the URL. There is no fallback now: if the token service
// is unavailable the caller shows an error instead of sending an unsafe link.

// ── Token-based short sign URL ─────────────────────────────────────────────
// Calls /api/sign-token (Vercel serverless) which generates a token, stores the
// payload server-side in Supabase, and returns the token. Result is a short URL
// like /sign?t=K7M2X9PQ — no PII in the URL.
//
// The serverless function uses the Supabase env vars (SUPABASE_URL,
// SUPABASE_ANON_KEY) configured in Vercel — no client-side config needed.
// The `sbConfig` argument is now ignored (kept for backwards compatibility).
export async function buildTokenSignUrl(invoicePayload, _sbConfig){
  // Slim payload — only what the sign page actually needs to display + sign.
  const slim = {
    invoiceNo:     invoicePayload.invoiceNo,
    fullName:      invoicePayload.fullName || invoicePayload.customerName,
    phone:         invoicePayload.phone    || invoicePayload.customerPhone,
    unit:          invoicePayload.unit,
    district:      invoicePayload.district,
    city:          invoicePayload.city,
    rent:          invoicePayload.rent,
    deposit:       invoicePayload.deposit,
    contractStart: invoicePayload.contractStart,
    contractEnd:   invoicePayload.contractEnd,
    lateDailyRate: invoicePayload.lateDailyRate,
    paymentMethod: invoicePayload.paymentMethod,
    nationality:   invoicePayload.nationality,
    idNumber:      invoicePayload.idNumber,
  };
  try {
    const r = await fetch('/api/sign-token', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ payload: slim }),
    });
    if (r.ok) {
      const data = await r.json();
      if (data?.token) return `${window.location.origin}/sign?t=${data.token}`;
    }
    console.warn('buildTokenSignUrl: /api/sign-token returned non-ok', r.status);
  } catch (e) {
    console.warn('buildTokenSignUrl: /api/sign-token threw', e);
  }
  // No fallback by design. Returning null makes the caller surface an error rather
  // than quietly sending a link with the customer's ID number inside it.
  console.error('buildTokenSignUrl failed — check /api/sign-token and SUPABASE_SERVICE_ROLE_KEY in Vercel');
  return null;
}

// Compute contract duration from start/end dates.
// Returns { ar, en } strings. Uses calendar-months when start & end fall on the
// same day-of-month (e.g. May 11 → June 11 = "شهر واحد"), otherwise day count.
export function computeContractDuration(startStr, endStr){
  if(!startStr || !endStr) return { ar: '', en: '' };
  const start = new Date(startStr);
  const end   = new Date(endStr);
  if(isNaN(start.getTime()) || isNaN(end.getTime()) || end <= start) return { ar: '', en: '' };

  const days = Math.round((end - start) / 86400000);

  // Calendar-month detection: same day-of-month → exact months
  if(start.getDate() === end.getDate()){
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    if(months > 0){
      if(months === 12) return { ar: 'سنة واحدة', en: 'One year' };
      if(months === 1)  return { ar: 'شهر واحد',  en: 'One month' };
      if(months === 2)  return { ar: 'شهران',     en: 'Two months' };
      if(months <= 10)  return { ar: `${months} أشهر`, en: `${months} months` };
      return                  { ar: `${months} شهراً`, en: `${months} months` };
    }
  }

  if(days === 1) return { ar: 'يوم واحد', en: '1 day' };
  if(days === 2) return { ar: 'يومان',    en: '2 days' };
  return         { ar: `${days} يوم`,     en: `${days} days` };
}

// Arabic WhatsApp message for signature requests (website + business name stay bilingual)
export function buildSignMessage(name, invoiceNo, signUrl){
  return (
    `مرحباً ${name}،\n\n` +
    `يرجى مراجعة وتوقيع فاتورة الإيجار رقم ${invoiceNo}.\n\n` +
    `✍️ رابط التوقيع:\n${signUrl}\n\n` +
    `🌐 www.findre.sa\n\n` +
    `شكراً،\n` +
    `مؤسسة فايند آر إي للعقارات · FindRE Real Estate\n` +
    `0551817337`
  );
}
export function buildWaUrl(phone, message){
  return `https://wa.me/${normalizePhoneForWa(phone)}?text=${encodeURIComponent(message)}`;
}

// WhatsApp message sent to customer AFTER payment is recorded — includes link
// to the same /sign?t=... URL which now serves the signed+paid receipt view.
export function buildPaidReceiptMessage(name, invoiceNo, signUrl){
  return (
    `مرحباً ${name}،\n\n` +
    `تم استلام دفعة فاتورة الإيجار رقم ${invoiceNo} بنجاح. شكراً لكم!\n\n` +
    `✅ النسخة النهائية الموقعة:\n${signUrl}\n\n` +
    `🌐 www.findre.sa\n\n` +
    `مع التقدير،\n` +
    `مؤسسة فايند آر إي للعقارات · FindRE Real Estate\n` +
    `0551817337`
  );
}

export function buildSignedPdfHtml(p, sigDataUrl){
  const GOLD="#ac9055", NAVY="#202a44";
  const fmt = n => Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});
  const rentN=Number(p.rent)||0, depN=Number(p.deposit)||0, lateN=Number(p.lateDailyRate)||100;
  const total = rentN + depN;
  const today = new Date().toISOString().slice(0,10);
  const dur   = computeContractDuration(p.contractStart, p.contractEnd);
  const LOGO_INLINE = `<div style="text-align:center">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 351.03 405.82" style="height:64px;width:auto;display:block;margin:0 auto">
      <path fill="${GOLD}" d="M301.04,323.82c15.41-20.82,23.82-46.17,23.82-72.15v-110.99L162.43,0,0,140.67v232.42h203.44c22.97,0,45.08-6.37,64.36-18.49l51.22,51.21,32.02-32.02-49.99-49.99ZM45.28,161.36l117.16-101.46,117.16,101.46v90.31c0,41.99-34.16,76.15-76.15,76.15H45.28v-166.46Z"/>
      <polygon fill="${GOLD}" points="212.22 220.53 212.22 209.01 212.22 164.98 168.2 164.98 112.65 164.98 112.65 220.53 112.65 264.56 156.67 264.56 168.2 264.56 212.22 264.56 212.22 220.53"/>
    </svg>
    <div style="font-family:'Tajawal',Arial,sans-serif;font-weight:800;font-size:20px;color:${NAVY};letter-spacing:-.5px;margin-top:6px;line-height:1">FindRE</div>
  </div>`;
  return `<!DOCTYPE html><html dir="rtl" lang="ar"><head><meta charset="UTF-8"/>
<title>Signed Invoice ${p.invoiceNo}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;800&display=swap');
*{box-sizing:border-box;margin:0;padding:0;-webkit-print-color-adjust:exact;print-color-adjust:exact}
body{font-family:'Tajawal',Arial,sans-serif;background:#fff;color:${NAVY};padding:32px 40px;direction:rtl;font-size:12px}
.header{text-align:center;border-bottom:1px solid #e8e0cc;padding-bottom:14px;margin-bottom:18px}
.header img{height:54px}
.title{text-align:center;font-size:22px;font-weight:800;margin:14px 0 18px}
.signed-badge{position:absolute;top:36px;left:40px;background:#22c55e;color:#fff;padding:6px 14px;border-radius:20px;font-size:11px;font-weight:800;transform:rotate(-8deg)}
.paid-stamp{width:90px;height:90px;object-fit:contain;opacity:0.92;transform:rotate(-12deg)}
table{width:100%;border-collapse:collapse;margin-bottom:14px}
td{padding:8px 12px;border:1px solid #d9d2c0;font-size:12px}
td.lbl{background:#faf8f3;width:140px;font-weight:700}
.totals{display:flex;justify-content:flex-start;margin:14px 0}
.totals table{width:auto;min-width:280px}
.totals td.amt{text-align:left;direction:ltr;font-weight:700}
.totals tr.grand td{border-top:2px solid ${GOLD};background:#faf8f3;color:${GOLD}}
.terms{background:#faf8f3;border:1px solid #e8e0cc;border-radius:6px;padding:10px 14px;font-size:11px;line-height:1.7;margin-bottom:18px}
.signatures{margin-top:18px;display:flex;align-items:center;gap:18px}
.sig-box{text-align:center;flex:0 0 280px}
.sig-img{display:block;height:90px;max-width:280px;margin:0 auto 6px;border-bottom:1.5px solid ${NAVY};object-fit:contain;background:#faf8f3}
.sig-label{font-size:11px;font-weight:700;color:${NAVY}}
.footer{margin-top:24px;padding-top:12px;border-top:1px solid #e8e0cc;text-align:center;font-size:10px;color:#79858d;line-height:1.6}
@media print{body{padding:18px 24px}@page{size:A4;margin:8mm}}
</style></head><body>
<div class="signed-badge">✓ موقّع</div>
<div class="header">${LOGO_INLINE}</div>
<div class="title">فاتورة موقّعة · Signed Invoice</div>

<table>
  <tr><td class="lbl">رقم الفاتورة</td><td style="color:${GOLD};font-weight:700;direction:ltr">${p.invoiceNo}</td><td class="lbl">التاريخ</td><td style="direction:ltr">${today}</td></tr>
  <tr><td class="lbl">العميل</td><td>${p.fullName||""}</td><td class="lbl">الجوال</td><td style="direction:ltr">${p.customerPhone||p.phone||""}</td></tr>
  <tr><td class="lbl">رقم الهوية</td><td style="direction:ltr">${p.idNumber||""}</td><td class="lbl">الجنسية</td><td>${p.nationality||""}</td></tr>
  <tr><td class="lbl">الوحدة</td><td>${p.unit||""} · ${p.district||""}</td><td class="lbl">آلية الدفع</td><td>${p.paymentMethod||""}</td></tr>
  <tr><td class="lbl">بداية العقد</td><td style="direction:ltr">${p.contractStart||""}</td><td class="lbl">نهاية العقد</td><td style="direction:ltr">${p.contractEnd||""}</td></tr>
  ${dur.ar?`<tr><td class="lbl">المدة</td><td>${dur.ar}</td><td class="lbl">Duration</td><td style="direction:ltr">${dur.en}</td></tr>`:""}
</table>

<table>
  <tr style="background:${NAVY};color:#fff"><td>الوصف</td><td>القيمة</td></tr>
  <tr><td>إيجار وحدة ${p.unit||""}</td><td style="direction:ltr">${fmt(rentN)} ر.س</td></tr>
  ${depN>0?`<tr><td>مبلغ التأمين (مسترد)</td><td style="direction:ltr">${fmt(depN)} ر.س</td></tr>`:""}
</table>

<div class="totals"><table>
  <tr><td class="lbl">المجموع الفرعي</td><td class="amt">${fmt(total)} ر.س</td></tr>
  <tr><td class="lbl">ضريبة القيمة المضافة</td><td class="amt">0.00 ر.س</td></tr>
  <tr class="grand"><td class="lbl">الإجمالي</td><td class="amt">${fmt(total)} ر.س</td></tr>
</table></div>

<div class="terms">
  <b>الشروط:</b>
  <ol style="padding-right:18px;margin-top:4px">
    <li>إخلاء الوحدة في تاريخ نهاية العقد (${p.contractEnd||"—"}) بدون إشعار إضافي.</li>
    <li>غرامة تأخير ${fmt(lateN)} ر.س عن كل يوم بعد نهاية العقد.</li>
    <li>التأمين مسترد بعد الإخلاء والفحص.</li>
    <li>الإيجار مستحق شهرياً حسب آلية الدفع المتفق عليها.</li>
  </ol>
</div>

<div class="signatures">
  <div class="sig-box">
    <img class="sig-img" src="${sigDataUrl}" alt=""/>
    <div class="sig-label">توقيع المستأجر · ${p.fullName||""}</div>
    <div style="font-size:10px;color:#79858d;margin-top:3px">تم التوقيع رقمياً في ${today}</div>
  </div>
  ${p.paidAt ? `<img class="paid-stamp" src="${typeof window !== 'undefined' ? window.location.origin : ''}/Paid-Stamp.png" alt="Paid Stamp"/>` : ''}
</div>

<div class="footer">
  <div style="color:${NAVY};font-weight:700">مؤسسة فايند آر إي للعقارات · FindRE Establishment For Real Estate</div>
  <div>0551817337 · info@findre.sa · CR 7043361620</div>
  <div style="margin-top:4px;font-size:9px">رقم الفاتورة · Invoice ${p.invoiceNo} — تم التوقيع رقمياً</div>
</div>
</body></html>`;
}
